import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, Sparkles, CreditCard, Calendar, Moon, Sun, Ticket, ChevronRight, Shield, ScrollText, MessageSquarePlus, X, Send, Cake, Pencil } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAppStore } from '../store/useAppStore'
import { api } from '../lib/api'

interface DreamItem {
  id: string
  animal: string
  feeling: string
  context: string
  lucky_numbers: number[]
  gemini_response?: string
  interpretation?: string
  created_at: string
}

interface UserStats {
  total_dreams: number
  total_payments: number
  member_since: string | null
  days_member: number
  recent_dreams: DreamItem[]
  recent_transactions: {
    ref_code: string
    amount_baht: number
    status: string
    created_at: string
    completed_at: string | null
  }[]
}

const ANIMAL_LABELS: Record<string, string> = {
  snake: '\u{1F40D} \u0E07\u0E39',
  fish: '\u{1F41F} \u0E1B\u0E25\u0E32',
  elephant: '\u{1F418} \u0E0A\u0E49\u0E32\u0E07',
  tiger: '\u{1F42F} \u0E40\u0E2A\u0E37\u0E2D',
  bird: '\u{1F426} \u0E19\u0E01',
  dog: '\u{1F415} \u0E2A\u0E38\u0E19\u0E31\u0E02',
  cat: '\u{1F408} \u0E41\u0E21\u0E27',
  human: '\u{1F464} \u0E04\u0E19',
  spirit: '\u{1F47B} \u0E27\u0E34\u0E0D\u0E0D\u0E32\u0E13',
  object: '\u{1F4E6} \u0E2A\u0E34\u0E48\u0E07\u0E02\u0E2D\u0E07',
  place: '\u{1F3DB} \u0E2A\u0E16\u0E32\u0E19\u0E17\u0E35\u0E48',
  other: '\u2728 \u0E2D\u0E37\u0E48\u0E19\u0E46',
}

const FEELING_LABELS: Record<string, string> = {
  fear: '\u0E01\u0E25\u0E31\u0E27',
  happy: '\u0E14\u0E35\u0E43\u0E08',
  confused: '\u0E2A\u0E31\u0E1A\u0E2A\u0E19',
  shocked: '\u0E15\u0E01\u0E43\u0E08',
  neutral: '\u0E40\u0E09\u0E22\u0E46',
  angry: '\u0E42\u0E01\u0E23\u0E18',
  sad: '\u0E40\u0E28\u0E23\u0E49\u0E32',
}

const CONTEXT_LABELS: Record<string, string> = {
  home: '\u0E17\u0E35\u0E48\u0E1A\u0E49\u0E32\u0E19',
  forest: '\u0E1B\u0E48\u0E32',
  water: '\u0E19\u0E49\u0E33/\u0E17\u0E30\u0E40\u0E25',
  temple: '\u0E27\u0E31\u0E14',
  still: '\u0E2D\u0E22\u0E39\u0E48\u0E19\u0E34\u0E48\u0E07\u0E46',
  chasing: '\u0E27\u0E34\u0E48\u0E07\u0E44\u0E25\u0E48',
  fighting: '\u0E15\u0E48\u0E2D\u0E2A\u0E39\u0E49',
  falling: '\u0E15\u0E01\u0E25\u0E07\u0E21\u0E32',
  lost: '\u0E2B\u0E25\u0E07\u0E17\u0E32\u0E07',
  flying: '\u0E1A\u0E34\u0E19',
}

function formatDate(s: string): string {
  const d = new Date(s)
  const months = ['\u0E21.\u0E04.','\u0E01.\u0E1E.','\u0E21\u0E35.\u0E04.','\u0E40\u0E21.\u0E22.','\u0E1E.\u0E04.','\u0E21\u0E34.\u0E22.','\u0E01.\u0E04.','\u0E2A.\u0E04.','\u0E01.\u0E22.','\u0E15.\u0E04.','\u0E1E.\u0E22.','\u0E18.\u0E04.']
  return `${d.getDate()} ${months[d.getMonth()]} ${(d.getFullYear() + 543).toString().slice(-2)}`
}

function formatTime(s: string): string {
  const d = new Date(s)
  return `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`
}

function StatBox({ icon, label, value, unit, highlight }: {
  icon: React.ReactNode; label: string; value: number; unit: string; highlight?: boolean
}) {
  return (
    <div className={`p-3 rounded-2xl flex flex-col items-center text-center ${highlight ? 'card-premium' : 'card-amulet'}`}>
      <div style={{ color: highlight ? 'var(--color-gold-dark)' : 'var(--color-text-muted)' }}>{icon}</div>
      <div className="text-lg font-bold mt-1" style={{ color: 'var(--color-text)' }}>{value}</div>
      <div className="text-[10px]" style={{ color: 'var(--color-text-soft)' }}>{label} ({unit})</div>
    </div>
  )
}

function SectionHeader({ title, icon }: { title: string; icon: string }) {
  return (
    <div className="px-4 mb-2 flex items-center gap-2">
      <span className="text-base">{icon}</span>
      <h2 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-gold-dark)' }}>
        {title}
      </h2>
    </div>
  )
}

function EmptyHint({ text }: { text: string }) {
  return <div className="text-center py-4 text-xs" style={{ color: 'var(--color-text-soft)' }}>{text}</div>
}

function ComingSoonCard({ icon, label, desc }: { icon: React.ReactNode; label: string; desc: string }) {
  return (
    <div className="p-3 rounded-2xl card-amulet opacity-60">
      <div className="flex items-center gap-2">
        <div style={{ color: 'var(--color-gold-dark)' }}>{icon}</div>
        <div className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>{label}</div>
      </div>
      <div className="text-[10px] mt-1" style={{ color: 'var(--color-text-soft)' }}>{desc}</div>
      <div className="text-[9px] mt-1.5 inline-block px-1.5 py-0.5 rounded"
           style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-muted)' }}>
        {'\u0E40\u0E23\u0E47\u0E27\u0E46 \u0E19\u0E35\u0E49'}
      </div>
    </div>
  )
}

const FEEDBACK_TYPES = [
  { value: 'suggestion', label: '\u{1F4A1} \u0E02\u0E49\u0E2D\u0E40\u0E2A\u0E19\u0E2D\u0E41\u0E19\u0E30' },
  { value: 'bug',        label: '\u{1F41B} \u0E23\u0E32\u0E22\u0E07\u0E32\u0E19\u0E1B\u0E31\u0E0D\u0E2B\u0E32' },
  { value: 'compliment', label: '\u2764\uFE0F \u0E0A\u0E21\u0E40\u0E0A\u0E22' },
  { value: 'other',      label: '\u{1F4AC} \u0E2D\u0E37\u0E48\u0E19\u0E46' },
]

function FeedbackModal({ onClose, displayName }: { onClose: () => void; displayName: string }) {
  const [type, setType] = useState('suggestion')
  const [text, setText] = useState('')
  const [sent, setSent] = useState(false)
  const [loading, setLoading] = useState(false)

  async function handleSend() {
    if (!text.trim()) return
    setLoading(true)
    const typeLabel = FEEDBACK_TYPES.find(t => t.value === type)?.label ?? type
    const msg = encodeURIComponent(`[Feedback: ${typeLabel}]\n\u0E08\u0E32\u0E01: ${displayName}\n\n${text.trim()}`)
    window.open(`https://line.me/R/ti/p/@538rqskd?text=${msg}`, '_blank')
    setLoading(false)
    setSent(true)
  }

  if (sent) {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center p-4"
           style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
        <div className="w-full max-w-md rounded-3xl p-8 text-center mb-4"
             style={{ background: 'var(--color-surface)' }} onClick={e => e.stopPropagation()}>
          <div className="text-5xl mb-3">{'\u{1F64F}'}</div>
          <h3 className="text-base font-bold mb-2" style={{ color: 'var(--color-text)' }}>
            {'\u0E02\u0E2D\u0E1A\u0E04\u0E38\u0E13\u0E21\u0E32\u0E01\u0E04\u0E23\u0E31\u0E1A!'}
          </h3>
          <p className="text-sm mb-5" style={{ color: 'var(--color-text-muted)' }}>
            {'Feedback \u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E21\u0E35\u0E04\u0E48\u0E32\u0E21\u0E32\u0E01\u0E2A\u0E33\u0E2B\u0E23\u0E31\u0E1A\u0E40\u0E23\u0E32'}
          </p>
          <button onClick={onClose} className="w-full py-3 rounded-xl font-semibold btn-gold">
            {'\u0E1B\u0E34\u0E14'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4"
         style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="w-full max-w-md rounded-3xl pb-6 mb-2"
           style={{ background: 'var(--color-surface)' }} onClick={e => e.stopPropagation()}>
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full" style={{ background: 'var(--color-surface-3)' }} />
        </div>
        <div className="flex items-center justify-between px-5 py-3">
          <div className="flex items-center gap-2">
            <MessageSquarePlus size={18} style={{ color: 'var(--color-gold-dark)' }} />
            <h3 className="text-sm font-bold" style={{ color: 'var(--color-text)' }}>
              {'\u0E2A\u0E48\u0E07 Feedback'}
            </h3>
          </div>
          <button onClick={onClose} style={{ color: 'var(--color-text-muted)' }}><X size={20} /></button>
        </div>
        <div className="px-5 flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-2">
            {FEEDBACK_TYPES.map(t => (
              <button key={t.value} onClick={() => setType(t.value)}
                      className="py-2.5 px-3 rounded-xl text-xs font-semibold transition-all"
                      style={{
                        background: type === t.value ? 'var(--gradient-gold)' : 'var(--color-surface-2)',
                        color: type === t.value ? '#fff' : 'var(--color-text-muted)',
                      }}>
                {t.label}
              </button>
            ))}
          </div>
          <textarea value={text} onChange={e => setText(e.target.value)}
                    placeholder={'\u0E1E\u0E34\u0E21\u0E1E\u0E4C\u0E02\u0E49\u0E2D\u0E04\u0E27\u0E32\u0E21\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E17\u0E35\u0E48\u0E19\u0E35\u0E48...'}
                    rows={4} maxLength={500}
                    className="w-full rounded-2xl p-4 text-sm resize-none outline-none"
                    style={{ background: 'var(--color-surface-2)', color: 'var(--color-text)', border: '1px solid var(--color-surface-3)' }} />
          <div className="text-right text-[10px]" style={{ color: 'var(--color-text-soft)', marginTop: -12 }}>
            {text.length}/500
          </div>
          <button onClick={handleSend} disabled={!text.trim() || loading}
                  className="w-full py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 btn-gold shimmer disabled:opacity-40">
            <Send size={16} />
            {loading ? '\u0E01\u0E33\u0E25\u0E31\u0E07\u0E2A\u0E48\u0E07...' : '\u0E2A\u0E48\u0E07 Feedback'}
          </button>
          <p className="text-center text-[10px]" style={{ color: 'var(--color-text-soft)' }}>
            {'Feedback \u0E08\u0E30\u0E16\u0E39\u0E01\u0E2A\u0E48\u0E07\u0E1C\u0E48\u0E32\u0E19 LINE OA \u0E02\u0E2D\u0E07\u0E40\u0E23\u0E32'}
          </p>
        </div>
      </div>
    </div>
  )
}

function BirthDateEditModal({ initial, lineUid, onClose, onSaved }: {
  initial: string | null
  lineUid: string
  onClose: () => void
  onSaved: () => void
}) {
  const today = new Date().toISOString().slice(0, 10)
  const [date, setDate] = useState(initial ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(date) && date <= today && date >= '1920-01-01'

  async function handleSave() {
    if (!valid) return
    setLoading(true)
    setError(null)
    try {
      await api.user.setBirthDate(lineUid, date)
      onSaved()
      onClose()
    } catch (err) {
      setError((err as Error).message || 'บันทึกไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
         style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div className="w-full max-w-sm rounded-3xl p-6"
           style={{ background: 'var(--color-surface)' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Cake size={18} style={{ color: 'var(--color-gold-dark)' }} />
            <h3 className="text-base font-bold" style={{ color: 'var(--color-text)' }}>
              {'แก้ไขวันเกิด'}
            </h3>
          </div>
          <button onClick={onClose} style={{ color: 'var(--color-text-muted)' }}><X size={20} /></button>
        </div>
        <p className="text-xs mb-3" style={{ color: 'var(--color-text-muted)' }}>
          {'ระบบจะใช้วันเกิดเพื่ออ่านดวงเฉพาะคุณ — แก้ไขแล้วดวงวันนี้จะ reset ใหม่'}
        </p>
        <input
          type="date"
          value={date}
          max={today}
          min="1920-01-01"
          onChange={e => setDate(e.target.value)}
          className="w-full px-4 py-3 rounded-2xl text-base outline-none mb-2"
          style={{
            background: 'var(--color-surface-2)',
            color: 'var(--color-text)',
            border: '1px solid var(--color-surface-3)',
          }}
        />
        {error && <p className="text-xs mb-2" style={{ color: '#e53e3e' }}>{error}</p>}
        <div className="flex gap-3 mt-4">
          <button onClick={onClose}
                  className="flex-1 py-3 rounded-xl font-semibold text-sm"
                  style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-muted)' }}>
            {'ยกเลิก'}
          </button>
          <button onClick={handleSave} disabled={!valid || loading}
                  className="flex-1 py-3 rounded-xl font-semibold text-sm btn-gold shimmer disabled:opacity-40">
            {loading ? 'กำลังบันทึก...' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  )
}

function formatBirthDate(s: string): string {
  const d = new Date(s + 'T00:00:00')
  const months = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.']
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear() + 543}`
}

function DreamDetailModal({ dream, onClose }: { dream: DreamItem; onClose: () => void }) {
  const interpretation = dream.gemini_response ?? dream.interpretation ?? '-'
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div className="w-full max-w-md rounded-3xl p-6 text-center max-h-[90vh] overflow-y-auto"
           style={{ background: 'var(--color-surface)' }} onClick={e => e.stopPropagation()}>
        <div className="text-4xl mb-2">{ANIMAL_LABELS[dream.animal]?.split(' ')[0] ?? '\u2728'}</div>
        <div className="text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}>
          {formatDate(dream.created_at)} \u2022 {formatTime(dream.created_at)}
        </div>
        <h3 className="text-base font-bold mb-3" style={{ color: 'var(--color-gold-dark)' }}>
          {'\u0E1D\u0E31\u0E19\u0E40\u0E2B\u0E47\u0E19 '}{ANIMAL_LABELS[dream.animal] ?? dream.animal}
        </h3>
        <div className="flex items-center justify-center gap-2 text-[11px] mb-4">
          <span className="px-2 py-0.5 rounded-full" style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-muted)' }}>
            {'\u{1F60A} '}{FEELING_LABELS[dream.feeling] ?? dream.feeling}
          </span>
          <span className="px-2 py-0.5 rounded-full" style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-muted)' }}>
            {'\u{1F319} '}{CONTEXT_LABELS[dream.context] ?? dream.context}
          </span>
        </div>
        <div className="p-4 rounded-2xl mb-4 text-sm leading-relaxed text-left"
             style={{ background: 'var(--color-surface-2)', color: 'var(--color-text)' }}>
          {interpretation}
        </div>
        <p className="text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>{'\u{1F340} \u0E40\u0E25\u0E02\u0E21\u0E07\u0E04\u0E25'}</p>
        <div className="flex justify-center gap-2 mb-5">
          {(dream.lucky_numbers ?? []).map((n, i) => (
            <div key={i} className="w-12 h-12 rounded-xl flex items-center justify-center text-base font-bold"
                 style={{ background: 'var(--gradient-gold)', color: '#fff' }}>
              {n.toString().padStart(2, '0')}
            </div>
          ))}
        </div>
        <button onClick={onClose} className="w-full py-3 rounded-xl font-semibold btn-gold shimmer">
          {'\u0E1B\u0E34\u0E14'}
        </button>
      </div>
    </div>
  )
}

export default function ProfilePage() {
  const navigate = useNavigate()
  const { profile, lineUid, theme, toggleTheme } = useAppStore()
  const [selectedDream, setSelectedDream] = useState<DreamItem | null>(null)
  const [showFeedback, setShowFeedback] = useState(false)
  const [showBirthEdit, setShowBirthEdit] = useState(false)

  const queryClient = useQueryClient()

  // Invalidate on mount เพื่อ sync ตั๋วที่อาจเปลี่ยนจากหน้าอื่น
  useEffect(() => {
    if (!lineUid) return
    queryClient.invalidateQueries({ queryKey: ['user', 'profile', lineUid] })
    queryClient.invalidateQueries({ queryKey: ['user', 'stats', lineUid] })
  }, [lineUid, queryClient])

  const { data: liveProfile } = useQuery({
    queryKey: ['user', 'profile', lineUid],
    queryFn: () => api.user.profile(lineUid!),
    enabled: !!lineUid,
    staleTime: 0,
  })

  const { data: stats } = useQuery({
    queryKey: ['user', 'stats', lineUid],
    queryFn: () => api.user.stats(lineUid!) as Promise<UserStats>,
    enabled: !!lineUid,
    staleTime: 1000 * 60,
  })

  const { data: savedNumbers, refetch: refetchSaved } = useQuery({
    queryKey: ['number', 'saved', lineUid],
    queryFn: () => api.number.list(lineUid!) as Promise<{ id: string; number: string; created_at: string }[]>,
    enabled: !!lineUid,
    staleTime: 0,
  })

  async function handleUnsave(number: string) {
    if (!lineUid) return
    try {
      await api.number.unsave(lineUid, number)
      refetchSaved()
    } catch {}
  }

  const tickets = (liveProfile as { dream_quota?: { paid_tickets?: number } } | undefined)?.dream_quota?.paid_tickets ?? profile?.dream_quota?.paid_tickets ?? 0
  const birthDate = (liveProfile as { birth_date?: string | null } | undefined)?.birth_date ?? null

  return (
    <div className="flex flex-col min-h-dvh pb-6" style={{ background: 'var(--color-bg)' }}>

      {selectedDream && <DreamDetailModal dream={selectedDream} onClose={() => setSelectedDream(null)} />}
      {showFeedback && (
        <FeedbackModal onClose={() => setShowFeedback(false)} displayName={profile?.display_name ?? 'User'} />
      )}
      {showBirthEdit && lineUid && (
        <BirthDateEditModal
          initial={birthDate}
          lineUid={lineUid}
          onClose={() => setShowBirthEdit(false)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['user', 'profile', lineUid] })
            queryClient.invalidateQueries({ queryKey: ['horoscope', 'today', lineUid] })
          }}
        />
      )}

      {/* Feedback FAB */}
      <button onClick={() => setShowFeedback(true)}
              className="fixed bottom-24 right-4 z-40 w-14 h-14 rounded-full flex items-center justify-center transition-transform active:scale-90"
              style={{ background: 'var(--gradient-gold)', boxShadow: '0 4px 20px rgba(184,148,90,0.45)' }}>
        <MessageSquarePlus size={22} color="#fff" />
      </button>

      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-6 pb-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/')} style={{ color: 'var(--color-text-muted)' }}>
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold" style={{ color: 'var(--color-text)' }}>
            {'\u0E42\u0E1B\u0E23\u0E44\u0E1F\u0E25\u0E4C'}
          </h1>
        </div>
        <button onClick={toggleTheme}
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: 'var(--color-surface)', border: '1px solid var(--color-surface-3)' }}>
          {theme === 'light'
            ? <Moon size={18} style={{ color: 'var(--color-gold-dark)' }} />
            : <Sun size={18} style={{ color: 'var(--color-gold)' }} />}
        </button>
      </div>

      {/* Profile Card */}
      <div className="mx-4 mb-4 p-5 card-amulet flex items-center gap-4">
        <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center"
             style={{ background: 'var(--gradient-gold)' }}>
          {profile?.avatar_url
            ? <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
            : <span className="text-3xl">{'\u{1FAB6}'}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-base truncate" style={{ color: 'var(--color-text)' }}>
            {profile?.display_name ?? 'Guest'}
          </div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
            {'\u0E2A\u0E21\u0E32\u0E0A\u0E34\u0E01\u0E15\u0E31\u0E49\u0E07\u0E41\u0E15\u0E48 '}{stats?.member_since ? formatDate(stats.member_since) : '\u2014'}
          </div>
          <div className="inline-flex items-center gap-1 mt-2 px-2.5 py-0.5 rounded-full text-[10px] font-semibold"
               style={{ background: 'var(--gradient-gold)', color: '#fff' }}>
            {'\u2B50 Free Tier'}
          </div>
        </div>
      </div>

      {/* Birth Date */}
      <div className="mx-4 mb-4">
        <button onClick={() => setShowBirthEdit(true)}
                className="w-full p-4 card-amulet flex items-center gap-3 text-left transition-transform active:scale-[0.98]">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-surface-2)' }}>
            <Cake size={18} style={{ color: 'var(--color-gold-dark)' }} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm" style={{ color: 'var(--color-text)' }}>
              {'วันเกิด'}
            </div>
            <div className="text-[11px]" style={{ color: birthDate ? 'var(--color-text-muted)' : '#e53e3e' }}>
              {birthDate ? formatBirthDate(birthDate) : 'ยังไม่ได้ตั้ง — กดเพื่อใส่วันเกิด'}
            </div>
          </div>
          <Pencil size={14} style={{ color: 'var(--color-text-soft)' }} />
        </button>
      </div>

      {/* Stats */}
      <div className="mx-4 mb-4 grid grid-cols-3 gap-2">
        <StatBox icon={<Sparkles size={18} />} label={'\u0E17\u0E33\u0E19\u0E32\u0E22\u0E1D\u0E31\u0E19'} value={stats?.total_dreams ?? 0} unit={'\u0E04\u0E23\u0E31\u0E49\u0E07'} />
        <StatBox icon={<Ticket size={18} />} label={'\u0E15\u0E31\u0E4B\u0E27\u0E04\u0E07\u0E40\u0E2B\u0E25\u0E37\u0E2D'} value={tickets} unit={'\u0E43\u0E1A'} highlight />
        <StatBox icon={<Calendar size={18} />} label={'\u0E40\u0E1B\u0E47\u0E19\u0E2A\u0E21\u0E32\u0E0A\u0E34\u0E01'} value={stats?.days_member ?? 0} unit={'\u0E27\u0E31\u0E19'} />
      </div>

      {/* Saved Numbers */}
      <SectionHeader title={'\u0E40\u0E25\u0E02\u0E40\u0E01\u0E47\u0E1A \u2B50'} icon={'\u{1F340}'} />
      <div className="mx-4 mb-4 card-amulet p-4">
        {savedNumbers && savedNumbers.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {savedNumbers.map(item => (
              <div key={item.id}
                   className="flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-xl"
                   style={{ background: 'var(--gradient-gold)', boxShadow: '0 2px 8px rgba(184,148,90,0.3)' }}>
                <span className="font-mono font-bold text-base text-white">{item.number}</span>
                <button
                  onClick={() => handleUnsave(item.number)}
                  className="w-5 h-5 rounded-full flex items-center justify-center transition-transform active:scale-90"
                  style={{ background: 'rgba(255,255,255,0.25)' }}>
                  <X size={10} color="#fff" strokeWidth={3} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-3">
            <p className="text-xs" style={{ color: 'var(--color-text-soft)' }}>
              {'\u0E22\u0E31\u0E07\u0E44\u0E21\u0E48\u0E21\u0E35\u0E40\u0E25\u0E02\u0E40\u0E01\u0E47\u0E1A \u2014 \u0E01\u0E14 \u2B50 \u0E17\u0E35\u0E48\u0E40\u0E25\u0E02\u0E21\u0E07\u0E04\u0E25\u0E43\u0E19\u0E2B\u0E19\u0E49\u0E32\u0E17\u0E33\u0E19\u0E32\u0E22\u0E1D\u0E31\u0E19'}
            </p>
          </div>
        )}
      </div>

      {/* Recent Dreams */}
      <SectionHeader title={'\u0E1D\u0E31\u0E19\u0E25\u0E48\u0E32\u0E2A\u0E38\u0E14'} icon={'\u{1F52E}'} />
      <div className="mx-4 mb-4 card-amulet p-3 flex flex-col gap-2">
        {stats?.recent_dreams && stats.recent_dreams.length > 0 ? (
          stats.recent_dreams.slice(0, 5).map(d => (
            <button key={d.id} onClick={() => setSelectedDream(d)}
                    className="flex items-center gap-3 p-2 rounded-xl text-left transition-transform active:scale-[0.98]"
                    style={{ background: 'var(--color-surface-2)' }}>
              <div className="text-2xl">{ANIMAL_LABELS[d.animal]?.split(' ')[0] ?? '\u2728'}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: 'var(--color-text)' }}>
                  {ANIMAL_LABELS[d.animal] ?? d.animal}
                </div>
                <div className="text-[10px]" style={{ color: 'var(--color-text-soft)' }}>
                  {formatDate(d.created_at)} \u2022 {formatTime(d.created_at)}
                </div>
              </div>
              <div className="flex gap-1">
                {(d.lucky_numbers ?? []).slice(0, 3).map((n, i) => (
                  <span key={i} className="w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold"
                        style={{ background: 'var(--gradient-gold)', color: '#fff' }}>
                    {n.toString().padStart(2, '0')}
                  </span>
                ))}
              </div>
              <ChevronRight size={14} style={{ color: 'var(--color-text-soft)' }} />
            </button>
          ))
        ) : (
          <EmptyHint text={'\u0E22\u0E31\u0E07\u0E44\u0E21\u0E48\u0E21\u0E35\u0E1B\u0E23\u0E30\u0E27\u0E31\u0E15\u0E34\u0E01\u0E32\u0E23\u0E17\u0E33\u0E19\u0E32\u0E22\u0E1D\u0E31\u0E19'} />
        )}
      </div>

      {/* Admin Panel */}
      {(profile?.role === 'admin' || profile?.role === 'super_admin') && (
        <div className="mx-4 mb-4">
          <button onClick={() => navigate('/admin/dashboard')}
                  className="w-full p-4 flex items-center gap-3 text-left transition-transform active:scale-[0.98] rounded-2xl"
                  style={{ background: 'linear-gradient(135deg,#1a1d18 0%,#2e3329 100%)', color: '#f0ead8', boxShadow: '0 4px 12px rgba(26,29,24,0.25)' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                 style={{ background: 'rgba(184,148,90,0.2)' }}>
              <Shield size={18} style={{ color: '#d4b884' }} />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-sm flex items-center gap-2">
                Admin Panel
                {profile?.role === 'super_admin' && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                        style={{ background: '#d4b884', color: '#1a1d18' }}>SUPER</span>
                )}
              </div>
              <div className="text-[11px] opacity-70">
                {'\u0E08\u0E31\u0E14\u0E01\u0E32\u0E23\u0E1C\u0E39\u0E49\u0E43\u0E0A\u0E49 \u0E18\u0E38\u0E23\u0E01\u0E23\u0E23\u0E21 \u0E41\u0E25\u0E30\u0E23\u0E30\u0E1A\u0E1A'}
              </div>
            </div>
            <ChevronRight size={16} style={{ color: '#d4b884' }} />
          </button>
        </div>
      )}

      {/* Payment History */}
      <div className="mx-4 mb-4">
        <button onClick={() => navigate('/profile/transactions')}
                className="w-full p-4 card-amulet flex items-center gap-3 text-left transition-transform active:scale-[0.98]">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-surface-2)' }}>
            <CreditCard size={18} style={{ color: 'var(--color-gold-dark)' }} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm" style={{ color: 'var(--color-text)' }}>
              {'\u0E1B\u0E23\u0E30\u0E27\u0E31\u0E15\u0E34\u0E01\u0E32\u0E23\u0E0A\u0E33\u0E23\u0E30\u0E40\u0E07\u0E34\u0E19'}
            </div>
            <div className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
              {'\u0E14\u0E39\u0E23\u0E32\u0E22\u0E01\u0E32\u0E23\u0E18\u0E38\u0E23\u0E01\u0E23\u0E23\u0E21\u0E17\u0E31\u0E49\u0E07\u0E2B\u0E21\u0E14 ('}{stats?.total_payments ?? 0}{' \u0E23\u0E32\u0E22\u0E01\u0E32\u0E23)'}
            </div>
          </div>
          <ChevronRight size={16} style={{ color: 'var(--color-text-soft)' }} />
        </button>
      </div>

      {/* Terms */}
      <div className="mx-4 mb-4">
        <button onClick={() => navigate('/terms')}
                className="w-full p-4 card-amulet flex items-center gap-3 text-left transition-transform active:scale-[0.98]">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-surface-2)' }}>
            <ScrollText size={18} style={{ color: 'var(--color-gold-dark)' }} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm" style={{ color: 'var(--color-text)' }}>
              {'\u0E02\u0E49\u0E2D\u0E01\u0E33\u0E2B\u0E19\u0E14\u0E01\u0E32\u0E23\u0E43\u0E0A\u0E49\u0E07\u0E32\u0E19'}
            </div>
            <div className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
              {'T&C \u2022 \u0E19\u0E42\u0E22\u0E1A\u0E32\u0E22\u0E04\u0E27\u0E32\u0E21\u0E40\u0E1B\u0E47\u0E19\u0E2A\u0E48\u0E27\u0E19\u0E15\u0E31\u0E27 \u2022 \u0E02\u0E49\u0E2D\u0E08\u0E33\u0E01\u0E31\u0E14\u0E04\u0E27\u0E32\u0E21\u0E23\u0E31\u0E1A\u0E1C\u0E34\u0E14\u0E0A\u0E2D\u0E1A'}
            </div>
          </div>
          <ChevronRight size={16} style={{ color: 'var(--color-text-soft)' }} />
        </button>
      </div>

      {/* Coming Soon */}
      <SectionHeader title={'\u0E01\u0E33\u0E25\u0E31\u0E07\u0E08\u0E30\u0E21\u0E32'} icon={'\u2728'} />
      <div className="mx-4 grid grid-cols-2 gap-2">
        <ComingSoonCard icon={'\u{1F3AF}'} label={'\u0E40\u0E25\u0E02\u0E2E\u0E34\u0E15'} desc={'\u0E41\u0E08\u0E49\u0E07\u0E40\u0E15\u0E37\u0E2D\u0E19\u0E17\u0E31\u0E19\u0E17\u0E35\u0E16\u0E49\u0E32\u0E40\u0E25\u0E02\u0E2D\u0E2D\u0E01'} />
        <ComingSoonCard icon={'\u{1F3C6}'} label={'Achievement'} desc={'\u0E20\u0E32\u0E23\u0E01\u0E34\u0E08\u0E2A\u0E30\u0E2A\u0E21'} />
        <ComingSoonCard icon={'\u{1F381}'} label={'Referral'} desc={'\u0E0A\u0E27\u0E19\u0E40\u0E1E\u0E37\u0E48\u0E2D\u0E19 \u0E23\u0E31\u0E1A\u0E1F\u0E23\u0E35'} />
        <ComingSoonCard icon={'\u{1F0CF}'} label={'\u0E44\u0E1E\u0E48\u0E22\u0E34\u0E1B\u0E0B\u0E35'} desc={'\u0E14\u0E39\u0E14\u0E27\u0E07\u0E44\u0E1E\u0E48\u0E22\u0E34\u0E1B\u0E0B\u0E35 3 \u0E43\u0E1A'} />
      </div>

      <div className="flex flex-col items-center gap-2 mt-6 mb-2 px-4">
        <button
          onClick={() => {
            localStorage.removeItem('__app_v')
            window.location.replace('/?r=' + Date.now())
          }}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-95"
          style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-muted)', border: '1px solid var(--color-surface-3)' }}
        >
          <span style={{ fontSize: '1rem', lineHeight: 1 }}>\u21bb</span>
          \u0e23\u0e35\u0e42\u0e2b\u0e25\u0e14\u0e41\u0e2d\u0e1b
        </button>
        <p className="text-[10px]" style={{ color: 'var(--color-text-soft)' }}>
          Amulet Digit \u2022 v1.0 MVP
        </p>
      </div>
    </div>
  )
}